import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import PFPartnerComponent from "./PFPartnerComponent";
import PJPartnerComponent from "./PJPartnerComponent";
import IdentificationSummary from "./IdentificationSummary";
import { validatePersonUniqueId } from "utils/pure-validation/primitiveValidators";
import { Grid } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { Autocomplete, CollapseCard, FakeText, Typography } from "@totalsoft/rocket-ui";
import { useTranslation } from "react-i18next";
import { emptyString } from "constants/variousTypes";
import {
  loadPersons,
  loadCitizenships,
  loadGenderTypes,
  loadMaritalStatuses,
  loadSpecialRelations,
  loadLegalForms,
  saveCompany,
  saveIndividual
} from "../../actionCreators";
import { getCitizenships, getGenderTypes, getMaritalStatuses, getSpecialRelations, getLegalForms } from "../../selectors";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { withParams } from "providers/ParamsConsumer";
import { isNil } from "lodash";

import { useApolloClient } from '@apollo/client';
import { clone } from "ramda";
import { getNodeAtPath } from "features/hierarchies/functions";

export const IdentificationContainer = ({
  partner,
  onPropertyChange,
  onPartnerPropertyChange,
  partnerTypes,
  defaultPartnerTypeId,
  onSexComputation,
  citizenships,
  genderTypes,
  genderTypesLoaded,
  maritalStatuses,
  specialRelations,
  legalForms,
  actions,
  onRedirect,
  saveDisabled,
  setSaveDisabled,
  validatePartner,
  validationPartner,
  dirtyInfo,
  setIsDirty,
  partnerLoaded,
  onFileSelected,
  hasWritePermission,
  isWaitingForOCR,
  resetDirtyInfo,
  personDocumentTypes,
  isIndividual,
  hidePartnerTypes,
  ...props
}) => {
  const { t } = useTranslation();
  const fileInputRef = React.createRef();
  const client = useApolloClient();
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (isNil(partner.partnerTypeId)) return;
    if ((partner.partnerTypeId & props.params.basePFTypeId) === props.params.basePFTypeId) {
      actions.loadCitizenships();
      actions.loadGenderTypes();
      actions.loadMaritalStatuses();
      actions.loadSpecialRelations();
    } else {
      actions.loadLegalForms();
    }
  }, [actions, partner.partnerTypeId, props.params.basePFTypeId]);

  const getGenderByUniqueId = (genderTypes, uniqueId) => {
    if (!uniqueId) return "";
    if (!validatePersonUniqueId(uniqueId, props.params.defaultCountryCode)) return "";
    if (!genderTypes || !genderTypes.length) return "";

    const maleDigits = ["1", "3", "5", "7"];
    const femaleDigits = ["2", "4", "6", "8"];
    const firstUniqueIdDigit = uniqueId.charAt(0);

    if (maleDigits.includes(firstUniqueIdDigit))
      return genderTypes.find(gen => gen.genderTypeCode === "M").genderTypeId;
    if (femaleDigits.includes(firstUniqueIdDigit))
      return genderTypes.find(gen => gen.genderTypeCode === "F").genderTypeId;
  };

  useEffect(() => {
    if (!(partnerLoaded && genderTypesLoaded) || !dirtyInfo?.uniqueId) return;
    if (
      !partner.isExternal &&
      partner.uniqueId &&
      (partner.partnerTypeId & props.params.basePFTypeId) === props.params.basePFTypeId
    ) {
      const genderId = getGenderByUniqueId(genderTypes, partner.uniqueId);
      onSexComputation(genderId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerLoaded, genderTypesLoaded, partner.isExternal, partner.uniqueId]);

  const loadPersons = search => {
    if (!search) {
      return Promise.resolve([]);
    }

    return actions.loadPersons(search).then(data => data.map(({ id, partnerName }) => ({ id, partnerName })));
  };

  const saveCompany = () => {
    if (!hasWritePermission) return;
    const { company, ...header } = partner;
    if (!validatePartner({ ...company, ...header })) {
      return;
    }
    actions.saveCompany({ ...company, ...header }, partner, afterSave);
    setSaveDisabled(true);
    setIsDirty(false);
  };

  const saveIndividual = () => {
    if (!hasWritePermission) return;
    const { individual, ...header } = partner;
    if (!validatePartner({ ...individual, ...header })) {
      return;
    }
    actions.saveIndividual({ ...individual, ...header }, partner, afterSave);
    setSaveDisabled(true);
    setIsDirty(false);
    resetDirtyInfo();
  };

  const afterSave = (partnerHierarchyData, partnerId) => {
    if(partnerHierarchyData)
      updatePartnerHierarchy(partnerHierarchyData);

    onRedirect(partnerId);
  }

  const updatePartnerHierarchy = (partnerHierarchyData) => {
    

    client.cache.modify({
      id: `PartnerHierarchy:{"partnerHierarchyId":${partnerHierarchyData.partnerHierarchyId}}`,
      fields: {
        detailsTree: previous => {
          const updatedPartnerHierarchyDetail = clone(previous);
          
          let detailToUpdate = getNodeAtPath(updatedPartnerHierarchyDetail, partnerHierarchyData.path);
          let newData = {partnerHierarchyDetailId: partnerHierarchyData.partnerHierarchyDetailId,
                        partnerHierarchyId: partnerHierarchyData.partnerHierarchyId,
                        category: partnerHierarchyData.category,
                        partnerId: partnerHierarchyData.partnerId,
                        parentDetailId: partnerHierarchyData.parentDetailId,
                        path: partnerHierarchyData.path,
                        partnerName: partnerHierarchyData.partnerName};

          detailToUpdate.data = { ...newData };
          return updatedPartnerHierarchyDetail;
        }
      }
    });
    client.cache.gc();

  }

  useEffect(() => {
    validatePartner({ ...partner, ...partner.individual, ...partner.company }, dirtyInfo);
  }, [partner, dirtyInfo, validatePartner]);

  const uploadClick = () => {
    fileInputRef.current.value = emptyString;
    fileInputRef.current.click();
  };

  return (
    <CollapseCard
      id={
        (partner.partnerTypeId & props.params.basePFTypeId) === props.params.basePFTypeId
          ? "pfPartnerCard"
          : "pjPartnerCard"
      }
      icon={PersonIcon}
      iconColor="primary"
      expanded={expanded}
      onToggle={(_, expanded) => setExpanded(!expanded)}
      title={t("Partner.IdentificationInfo")}
      toggleOnHeaderClick
      subheader={
        expanded ? (
          <></>
        ) : (
          <IdentificationSummary
            partner={partner}
            partnerTypes={partnerTypes}
            defaultPartnerTypeId={defaultPartnerTypeId}
            basePFTypeId={props.params.basePFTypeId}
          />
        )
      }
    >
      <Grid container spacing={1}>
        {isWaitingForOCR && (
          <Grid item xs={12}>
            <Typography>{t("Partner.WaitingForOCR")}</Typography>
          </Grid>
        )}
        {!hidePartnerTypes && (
          <>
            <Grid item xs={12} sm={12} md={4}>
              <Autocomplete
                required
                fullWidth
                id="identification_partnerType"
                label={t("Partner.PartnerType")}
                value={partnerTypes.length > 0 ? partner.partnerTypeId || defaultPartnerTypeId || "" : ""}
                valueKey="partnerTypeId"
                labelKey="partnerTypeName"
                simpleValue={true}
                onChange={onPartnerPropertyChange("partnerTypeId")}
                options={partnerTypes}
                disabled={partner.chPartnerId || (partner.partnerId && isIndividual) || !hasWritePermission}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={8}></Grid>
          </>
        )}
        {partner.partnerTypeId == null ? (
          <FakeText />
        ) : (partner.partnerTypeId & props.params.basePFTypeId) === props.params.basePFTypeId ? (
          <PFPartnerComponent
            validationPartner={validationPartner}
            partner={partner}
            loadPersons={loadPersons}
            citizenships={citizenships}
            genderTypes={genderTypes}
            personDocumentTypes={personDocumentTypes}
            maritalStatuses={maritalStatuses}
            specialRelations={specialRelations}
            onPartnerPropertyChange={onPartnerPropertyChange}
            onPropertyChange={onPropertyChange("individual")}
            onSave={saveIndividual}
            saveDisabled={saveDisabled}
            uploadClick={uploadClick}
            fileInputRef={fileInputRef}
            onFileSelected={onFileSelected}
            hasWritePermission={hasWritePermission}
          />
        ) : (
          <PJPartnerComponent
            id="pjPartner"
            partner={partner}
            legalForms={legalForms}
            onPartnerPropertyChange={onPartnerPropertyChange}
            onPropertyChange={onPropertyChange("company")}
            onSave={saveCompany}
            saveDisabled={saveDisabled}
            validationPartner={validationPartner}
            hasWritePermission={hasWritePermission}
          />
        )}
      </Grid>
    </CollapseCard>
  );
};

IdentificationContainer.propTypes = {
  partner: PropTypes.object.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onPartnerPropertyChange: PropTypes.func.isRequired,
  defaultPartnerTypeId: PropTypes.string,
  partnerTypes: PropTypes.array.isRequired,
  citizenships: PropTypes.array.isRequired,
  genderTypes: PropTypes.array.isRequired,
  genderTypesLoaded: PropTypes.bool.isRequired,
  personDocumentTypes: PropTypes.array.isRequired,
  maritalStatuses: PropTypes.array.isRequired,
  specialRelations: PropTypes.array.isRequired,
  legalForms: PropTypes.array.isRequired,
  actions: PropTypes.object.isRequired,
  onRedirect: PropTypes.func.isRequired,
  saveDisabled: PropTypes.bool,
  setSaveDisabled: PropTypes.func,
  onSexComputation: PropTypes.func,
  validationPartner: PropTypes.object.isRequired,
  validatePartner: PropTypes.func.isRequired,
  dirtyInfo: PropTypes.object.isRequired,
  setIsDirty: PropTypes.func,
  partnerLoaded: PropTypes.bool.isRequired,
  onFileSelected: PropTypes.func,
  hasWritePermission: PropTypes.bool.isRequired,
  isWaitingForOCR: PropTypes.bool.isRequired,
  resetDirtyInfo: PropTypes.func,
  isIndividual: PropTypes.bool.isRequired,
  hidePartnerTypes: PropTypes.bool.isRequired,
  params: PropTypes.object
};

function mapStateToProps(state) {
  const citizenships = getCitizenships(state);
  const maritalStatuses = getMaritalStatuses(state);
  const specialRelations = getSpecialRelations(state);
  const legalForms = getLegalForms(state);
  const { content: genderTypes, loaded: genderTypesLoaded } = getGenderTypes(state);

  return {
    citizenships,
    genderTypes,
    genderTypesLoaded,
    maritalStatuses,
    specialRelations,
    legalForms
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadPersons,
        loadCitizenships,
        loadGenderTypes,
        loadMaritalStatuses,
        loadSpecialRelations,
        loadLegalForms,
        saveCompany,
        saveIndividual
      },
      dispatch
    ),
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(withParams(IdentificationContainer));
